import { sdkRequest, translateServerErrors } from '@eb/http';
import { EVENT_ID_REQUIRED_ERROR, ID_REQUIRED_ERROR } from '../constants';
import { mapSchedulePrivacySettingsFromApi } from '../utils';

/**
 * Return a string to use for storing and retrieving event privacy schedule settings from resourceCache
 * @param eventId - the public id of the event
 * @returns {string}
 */
export const getSchedulePrivacySettingsCacheKey = (eventId: string) =>
    `eventPrivacySchedules/${eventId}`;

/**
 * Return a string adding eventId to it, in order to use it as URL for sdkRequest
 * @param eventId - the public id of the event
 * @returns {string}
 */
const getSchedulePrivacySettingsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/event_privacy_schedules/`;

/**
 * Return a string adding id of event_privacy_schedule to it, in order to use it as URL for sdkRequest
 * @param id - the id of the event_privacy_schedule register
 * @returns {string}
 */
const deleteSchedulePrivacySettingsUrl = (id: string): string =>
    `/api/v3/event_privacy_schedules/${id}/`;

/**
 * Return a `fetch` Promise to save event event schedule privacy settings data
 *
 * @param {object} data - event privacy schedule settings data to save to the api
 * @param {object} errorSpec - error specification provided to `translateServerErrors` in the event of a failing
 *                             response
 * @param {string} eventId - the event to update
 * @returns {Promise}
 */
export const schedulePublishToPublicEvent = (
    data: any,
    errorSpec: any,
    eventId: string,
) => {
    if (!eventId) {
        throw new Error(EVENT_ID_REQUIRED_ERROR);
    }

    const options = {
        method: 'POST',
        body: JSON.stringify(data),
    };

    return sdkRequest(getSchedulePrivacySettingsUrl(eventId), options).catch(
        translateServerErrors(errorSpec),
    );
};

/**
 * Return a `fetch` Promise to get event schedule privacy settings data
 *
 * @param {object} errorSpec - error specification provided to `translateServerErrors` in the event of a failing
 *                             response
 * @param {string} eventId - the event to update
 * @returns {Promise}
 */

export const getEventScheduleToPublish = (
    errorSpec: any,
    eventId: string,
    timezone: string,
) => {
    if (!eventId) {
        throw new Error(EVENT_ID_REQUIRED_ERROR);
    }

    return sdkRequest(getSchedulePrivacySettingsUrl(eventId))
        .then((response: any) =>
            mapSchedulePrivacySettingsFromApi(response, timezone),
        )
        .catch(translateServerErrors(errorSpec));
};

/**
 * Return a `delete` Promise to get event schedule privacy settings data
 *
 * @param {object} errorSpec - error specification provided to `translateServerErrors` in the event of a failing
 *                             response
 * @param {string} eventId - the id of event_privacy_schedule register, where is set the timer to delete
 * @returns {Promise}
 */

export const deleteEventSchedulePrivateToPublish = (
    errorSpec: any,
    id: string,
): Promise<any> => {
    if (!id) {
        throw new Error(ID_REQUIRED_ERROR);
    }

    const options = {
        method: 'DELETE',
    };

    return sdkRequest(deleteSchedulePrivacySettingsUrl(id), options).catch(
        translateServerErrors(errorSpec),
    );
};
